"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawingBackground = void 0;
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
const AutoDrawing_1 = require("../objects/AutoDrawing");
const gsap_1 = require("gsap");
const Global_1 = require("../Global");
const lodash_1 = __importDefault(require("lodash"));
const index_1 = require("./index");
class DrawingBackground extends index_1.Block {
    constructor(app, total) {
        super(app);
        this.total = total;
        this.speed = 0.0048;
        this.size = [0.55, 0.70];
        this.strokeWidth = [10, 14];
        this.nextColor = () => {
            return (0, Global_1.randomColor)();
        };
        this.promises = null;
        this.playing = false;
        this.layout();
    }
    start() {
        if (!this.playing) {
            this.playing = true;
            // Make a few graphics
            this.promises = new Array(this.total);
            for (let i = 0; i < this.total; i++) {
                this.promises[i] = Promise.resolve();
                this.chainGraphic(i).then();
            }
        }
    }
    stop() {
        if (this.playing) {
            // clear everything
            while (this.children[0]) {
                (this.children[0]).destroy(true);
            }
            this.removeChildren();
            this.playing = false;
            this.promises = null;
        }
    }
    get isPlaying() {
        return this.playing;
    }
    chainGraphic(i) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.promises[i];
            if (this.playing) {
                const graphic = this.generate();
                return this.timelinify(graphic).then(() => {
                    // Check and make sure that we were not already detached
                    if (graphic.parent === this) {
                        graphic === null || graphic === void 0 ? void 0 : graphic.destroy(true);
                    }
                    if (this.playing) {
                        this.promises[i] = this.chainGraphic(i);
                    }
                });
            }
        });
    }
    destroy(_options) {
        super.destroy(_options);
    }
    timelinify(graphic) {
        return new Promise((resolve) => {
            const ease = 'power2.in';
            const fadetimeline = gsap_1.gsap.timeline({
                autoRemoveChildren: true
            }).to(graphic, {
                pixi: {
                    scale: (graphic.scale.x + graphic.scale.y) / 2 / 1.5
                },
                ease: ease,
                duration: graphic.timeline.totalDuration()
            }).to(graphic.blurFilter, {
                blur: (0, Global_1.px)(15),
                duration: graphic.timeline.totalDuration(),
                ease: ease
            }, '<').to(graphic, {
                pixi: {
                    alpha: 0.0
                },
                duration: 2.0,
                onComplete: () => {
                    return resolve(graphic);
                }
            }, '-=50%');
            graphic.timeline.add(fadetimeline, '-=50%').play();
        });
    }
    generate(strokes = AssetManager_1.default.randomStrokes()) {
        const graphic = new AutoDrawing_1.AutoDrawing(strokes, this.speed, this.nextColor(), lodash_1.default.random(this.strokeWidth[0], this.strokeWidth[1]));
        graphic.pivot.set(graphic.width / 2, graphic.height / 2);
        graphic.scale.set(lodash_1.default.random(this.size[0], this.size[1]));
        const sw = (graphic.width * graphic.scale.x) / 2;
        const sh = (graphic.height * graphic.scale.y) / 2;
        const b = graphic.blurFilter.blur;
        let breaker = 0; // Just in case this takes too long
        do {
            graphic.x = lodash_1.default.random(sw + b, this.drawWidth - sw - b);
            graphic.y = lodash_1.default.random(sh + b, this.drawHeight - sh - b);
        } while (breaker++ < 250 && this.hitTest(graphic));
        return this.addChild(graphic);
    }
    hitTest(me) {
        for (let i = 0; i < this.children.length; i++) {
            const them = (this.children[i]);
            if (them instanceof AutoDrawing_1.AutoDrawing && me.x < them.x + them.width * them.scale.x
                && me.x + me.width * me.scale.x > them.x
                && me.y < them.y + them.height * them.scale.y
                && me.y + me.height * me.scale.y > them.y) {
                return true;
            }
        }
        return false;
    }
    setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY) {
        return super.setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY);
    }
    layout() {
        super.layout();
        const top = (0, Global_1.px)(50);
        const bottom = (0, Global_1.px)(60); // Maybe in future read from navbar
        this.drawWidth = this.app.screen.width;
        this.drawHeight = this.app.screen.height - top - bottom;
        this.y = top;
    }
}
exports.DrawingBackground = DrawingBackground;
